import { Suspense, useEffect, useRef } from "react";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { usePressClassificationRoles } from "@libs/roles/hooks/usePressClassificationRoles";
import { usePressIdFromURL } from "@utils/getStateFromURL";

import * as S from "./UnsuitableButton.style";
import { useHandleUnsuitablePress } from "../../hooks/useHandleUnsuitablePress";
import {
  UnsuitableReasonModalPosition,
  UnsuitableReasonUsageType,
} from "../../types/unsuitableReason.types";
import UnsuitableReasonModalContainer from "../UnsuitableReasonModalContainer/UnsuitableReasonModalContainer";

type Props = {
  type?: UnsuitableReasonUsageType;
  modalPosition?: UnsuitableReasonModalPosition;
};

const TYPE_TO_TEXT: Record<UnsuitableReasonUsageType, string> = {
  PRESS_DETAIL: "Unsuitable / Review Required",
  CTG_DETAIL: "The Unsuitable Registry",
  PRESS_LIST: "The Unsuitable Press",
  CTG_LIST: "The Unsuitable Registry",
  PUBLICATION_DETAIL: "The Unsuitable Publication",
  PUBLICATION_LIST: "The Unsuitable Publication",
};

const UnsuitableButton = ({ type = "PRESS_DETAIL", modalPosition }: Props) => {
  const draftArticleId = usePressIdFromURL();
  const { data: articleInfo } = useArticleInfoQuery(Number(draftArticleId));
  const unsuitableRef = useRef<HTMLDivElement>(null);

  const { hasUnsuitablePressRoles } = usePressClassificationRoles();

  const ROLES_BY_TYPE = {
    PRESS_DETAIL: hasUnsuitablePressRoles,
    CTG_DETAIL: hasUnsuitablePressRoles,
    PRESS_LIST: hasUnsuitablePressRoles,
    CTG_LIST: hasUnsuitablePressRoles,
    PUBLICATION_DETAIL: hasUnsuitablePressRoles,
    PUBLICATION_LIST: hasUnsuitablePressRoles,
  };

  const duplArticleId = articleInfo?.duplArticleId || null;
  const articleRejectReason = articleInfo?.articleRejectReason || null;
  const isReviewRequired =
    ["PRESS_DETAIL", "PRESS_LIST"].includes(type) &&
    articleInfo?.isReviewRequired === true;
  const isRejectedReason = !!articleRejectReason && !isReviewRequired;

  const targetRejectReason = articleRejectReason
    ? `${articleRejectReason}${duplArticleId ? ` (${duplArticleId})` : ""}`
    : null;

  const {
    openModalUnsuitablePress,
    closeModalUnsuitablePress,
    isOpenModalUnsuitablePress,
  } = useHandleUnsuitablePress({ type });

  const handleUnsuitableButtonActive = async () => {
    if (isOpenModalUnsuitablePress) return closeModalUnsuitablePress();

    openModalUnsuitablePress();
  };

  useEffect(() => {
    const handleCheckClickOutsideUnsuitable = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickReasonButton = unsuitableRef.current?.contains(target);
      const deleteReasonButton = target.dataset.button;

      if (clickReasonButton) return;
      if (deleteReasonButton) return;

      closeModalUnsuitablePress();
    };

    window.addEventListener("click", handleCheckClickOutsideUnsuitable);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideUnsuitable);
  }, [closeModalUnsuitablePress]);

  return (
    <>
      <S.UnsuitableButtonWrapper
        ref={unsuitableRef}
        isEnableUnsuitablePress={isRejectedReason}
        disabled={!hasUnsuitablePressRoles}
      >
        <S.UnsuitableButton
          type="button"
          onClick={handleUnsuitableButtonActive}
          isEnableUnsuitablePress={isRejectedReason}
          disabled={!ROLES_BY_TYPE[type]}
          isReviewRequired={isReviewRequired}
        >
          <>
            {isRejectedReason
              ? targetRejectReason
              : isReviewRequired
              ? `Overlapped Id: #${articleInfo.reviewIdList?.join(
                  ", #",
                )} (Review Required)`
              : TYPE_TO_TEXT[type]}
          </>
        </S.UnsuitableButton>
        <Suspense fallback={<></>}>
          <UnsuitableReasonModalContainer
            closeModalUnsuitablePress={closeModalUnsuitablePress}
            isOpenModalUnsuitablePress={isOpenModalUnsuitablePress}
            articleRejectReason={articleRejectReason}
            unsuitableReasonUsageType={type}
            draftArticleId={draftArticleId}
            modalPosition={modalPosition}
          />
        </Suspense>
      </S.UnsuitableButtonWrapper>
    </>
  );
};

export default UnsuitableButton;
